import React from 'react';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { CartProvider } from '@/contexts/CartContext';
import { ClinicProvider } from '@/contexts/ClinicContext';
import { PendingPrescriptionProvider } from '@/contexts/PendingPrescriptionContext';
import { FHIRProvider } from '@/contexts/FHIRContext';
import { OrganizationProvider } from '@/contexts/OrganizationContext';
import { IntelliHealthChatProvider } from '@/contexts/IntelliHealthChatContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import ProtectedRoutePractitioner from '@/components/ProtectedRoutePractitioner';
import StaffProtectedRoute from '@/components/StaffProtectedRoute';
import 'react-day-picker/style.css';
import '@/App.css';

import Clinic from '@/pages/Clinic';
import NotFound from '@/pages/NotFound';
import Login from '@/pages/Login';
import ResetPassword from '@/pages/ResetPassword';
import Invite from '@/pages/Invite';
import ClinicSelection from '@/pages/ClinicSelection';

// Onboarding
import PractitionerOnboarding from '@/pages/PractitionerOnboarding';
import PractitionerOnboardingSuccess from '@/pages/PractitionerOnboardingSuccess';
import ClinicOnboarding from '@/pages/ClinicOnboarding';
import ClinicOnboardingSuccess from '@/pages/ClinicOnboardingSuccess';
import PartialClinicOnboarding from '@/pages/PartialClinicOnboarding';

// Dashboard / Layout
import ClinicDashboard from '@/components/clinic/ClinicDashboard';
import PractitionerPortal from '@/pages/PrectitionerPortal';
import PractitionerDashboard from '@/components/clinic/PractitionerDashboard';
import ProviderDashboard from '@/components/clinic/ProviderDashboard';
import MyDayDashboard from '@/components/clinic/MyDayDashboard';
import PatientChart from '@/pages/PatientChart';
import ClinicStaffPortal from '@/pages/ClinicStaffPortal';
import OrgLayout from '@/pages/OrgLayout';

// Storefront
import Storefront from '@/pages/Storefront';
import StorefrontPage from '@/pages/StorefrontPage';
import StorefrontConfirmation from '@/pages/StorefrontConfirmation';
import ProgramDetail from '@/pages/ProgramDetail';
import Cart from '@/pages/Cart';
import Checkout from '@/pages/Checkout';
import PrescriptionConfirmation from '@/pages/PrescriptionConfirmation';
import PrescriptionLink from '@/pages/PrescriptionLink';
import GuidedOrders from '@/pages/GuidedOrders';
import GuidedOrderAmendment from '@/pages/GuidedOrderAmendment';

// Patients / Practitioners
import ClinicPatients from '@/components/clinic/ClinicPatients';
import ExternalPatients from '@/pages/ExternalPatients';
import PatientDetail from '@/pages/PatientDetail';
import PatientViewRouter from '@/components/PatientViewRouter';
import RoleAwareIndex from '@/components/RoleAwareIndex';
import VisitCockpit from '@/pages/VisitCockpit';
import PatientAvs from '@/pages/PatientAvs';
import PractitionersPage from '@/pages/PractitionersPage';
import PractitionerDetail from '@/pages/PractitionerDetail';
import PractitionerStaffManagement from '@/pages/PractitionerStaffManagement';

// Clinic features
import ClinicPrescriptions from '@/components/clinic/ClinicPrescriptions';
import ClinicPayments from '@/components/clinic/ClinicPayments';
import ClinicProducts from '@/components/clinic/ClinicProducts';
import ClinicProgramsPage from '@/pages/ClinicProgramsPage';
import Subscriptions from '@/pages/Subscriptions';
import Intellihealth from '@/components/clinic/Intellihealth';

// Settings
import Settings from '@/pages/Settings';
import CalendarSettingsPage from '@/pages/settings/CalendarSettingsPage';
import ConsentFormsPage from '@/pages/settings/ConsentFormsPage';
import IntakeFormTemplatesPage from '@/pages/settings/IntakeFormTemplatesPage';
import CustomIntake from '@/pages/CustomIntake';
import WebhooksPage from '@/pages/settings/WebhooksPage';
import WebhookDetailPage from '@/pages/settings/WebhookDetailPage';
import AvailabilitySettings from '@/pages/AvailabilitySettings';
import BookingDashboard from '@/pages/BookingDashboard';
import AppointmentTypesPage from '@/pages/AppointmentTypesPage';
import PracticeProductsPage from '@/pages/PracticeProductsPage';
import InvoicesPage from '@/pages/InvoicesPage';
import InvoiceDashboardPage from '@/pages/InvoiceDashboardPage';
import PatientPaymentPage from '@/pages/PatientPaymentPage';

// Admin / Roles
import RoleManagement from '@/pages/RoleManagement';
import UserManagement from '@/pages/UserManagement';
import AdminManagement from '@/pages/AdminManagement';

// Consolidated admin pages
import StaffPage from '@/pages/admin/StaffPage';
import FinancialsPage from '@/pages/admin/FinancialsPage';
import ProgramsPage from '@/pages/admin/ProgramsPage';

// Commissions / Referrals
import {
  CommissionConfigPage,
  CommissionStatementsPage,
  CommissionPayoutsPage,
  CommissionPayoutDetailPage,
} from '@/components/commissions';

// Partner API failures (clinic admin "Integration Issues" tab — Nimbus)
import PartnerFailures from '@/components/clinic/PartnerFailures';

// Intake
import PatientFormPublic from '@/pages/PatientFormPublic';
import PatientIntakeDefault, { PatientIntakeForm } from '@/pages/PatientIntake';
import SmartIntake from '@/pages/SmartIntake';
import { WomensIntakeLanding, WomensIntakeForm } from '@/pages/WomensIntake';
import { MensIntakeLanding, MensIntakeForm } from '@/pages/MensIntake';
import { WeightLossIntakeLanding } from '@/pages/WeightLossIntake';
import { Glp1QuizIntakeForm } from '@/pages/Glp1QuizIntake';
import IntakeDashboard from '@/pages/IntakeDashboard';

// PCOS
import { PCOSLanding, PCOSQuiz, PCOSResults } from '@/pages/pcos';

// EMR / Telehealth / Messaging
import { EmrCalendarPage } from '@/pages/emr';
import { ProviderVideoCall, PatientJoin } from '@/pages/telehealth';
import Messaging from '@/pages/Messaging';
import PatientChat from '@/pages/PatientChat';

// Tasks
import TasksPage from '@/pages/TasksPage';

// Labs
import { LabOrdersPage, LabResultsPage } from '@/pages/labs';
import LabExtractionReviewPage from '@/pages/labs/LabExtractionReviewPage';
import LabExtractionReviewSheet from '@/components/labs/LabExtractionReviewSheet';

// Booking
import PublicBookingPage from '@/pages/PublicBookingPage';
import PublicBookingV2Page from '@/pages/PublicBookingV2Page';
import PublicBookingClinicLandingPage from '@/pages/PublicBookingClinicLandingPage';

// SMART / FHIR
import SMARTLaunch from '@/pages/SMARTLaunch';

// Patient links / consent
import PatientLinkRouter from '@/pages/patient/PatientLinkRouter';
import PatientConsentSigningPage from '@/pages/patient/PatientConsentSigningPage';

// MDI Portal
import MdiPortal from '@/pages/public/MdiPortal';

// Test
import TestBookingWidget from '@/pages/test/TestBookingWidget';

// Org pages
import TrialSignup from '@/pages/TrialSignup';

import OrgDashboard from '@/pages/org/OrgDashboard';
import OrgClinics from '@/pages/org/OrgClinics';
import OrgMembers from '@/pages/org/OrgMembers';
import OrgSettings from '@/pages/org/OrgSettings';
import OrgAuditLog from '@/pages/org/OrgAuditLog';
import OrgBilling from '@/pages/org/OrgBilling';

// Dev-only
const LabRangeBarDemo = import.meta.env.DEV
  ? React.lazy(() => import("./components/intellihealth/LabRangeBarDemo"))
  : () => null;

// ---------------------------------------------------------------------------
// Shared route sets — reused across clinic, practitioner-portal, staff-portal
// ---------------------------------------------------------------------------
const StorefrontRoutes = () => (
  <>
    <Route path="storefront" element={<Storefront />} />
    <Route path="storefront/program/:programId" element={<ProgramDetail />} />
    <Route path="storefront/cart" element={<Cart />} />
    <Route path="storefront/checkout" element={<Checkout />} />
    <Route path="prescription/:prescriptionId" element={<PrescriptionConfirmation />} />
    <Route path="storefront/:programId" element={<StorefrontPage />} />
    <Route path="storefront/confirmation/:orderId" element={<StorefrontConfirmation />} />
    <Route path="guided-orders" element={<GuidedOrders />} />
    <Route path="guided-orders/:orderId" element={<GuidedOrders />} />
  </>
);

const CommonClinicRoutes = ({ settingsRedirect }: { settingsRedirect: string }) => (
  <>
    <Route path="patients" element={<ClinicPatients />} />
    <Route path="external-patients" element={<ExternalPatients />} />
    <Route path="external-patients/:id" element={<PatientViewRouter />} />
    <Route path="patients/:id" element={<PatientViewRouter />} />
    <Route path="prescriptions" element={<ClinicPrescriptions />} />
    <Route path="payments" element={<ClinicPayments />} />
    <Route path="products" element={<ClinicProducts />} />
    <Route path="settings" element={<Settings />} />
    <Route path="settings/calendar" element={<CalendarSettingsPage />} />
    <Route path="settings/availability" element={<AvailabilitySettings />} />
    <Route path="settings/booking" element={<BookingDashboard />} />
    <Route path="settings/appointment-types" element={<AppointmentTypesPage />} />
    <Route path="settings/practice-products" element={<PracticeProductsPage />} />
    <Route path="invoices" element={<InvoicesPage />} />
    <Route path="invoices/dashboard" element={<InvoiceDashboardPage />} />
    <Route path="settings/consents" element={<ConsentFormsPage />} />
    <Route path="settings/intake-forms" element={<IntakeFormTemplatesPage />} />
    <Route path="settings/webhooks" element={<WebhooksPage />} />
    <Route path="settings/webhooks/:webhookId" element={<WebhookDetailPage />} />
    <Route path="preferences" element={<Navigate to={settingsRedirect} replace />} />
    <Route path="intakes" element={<IntakeDashboard />} />
    <Route path="emr/calendar" element={<EmrCalendarPage />} />
    <Route path="visit/:encounterId" element={<VisitCockpit />} />
    <Route path="telehealth/call/:appointmentId" element={<ProviderVideoCall />} />
    <Route path="messaging" element={<Messaging />} />
    <Route path="tasks" element={<TasksPage />} />
    <Route path="labs" element={<LabOrdersPage />} />
    <Route path="labs/orders" element={<LabOrdersPage />} />
    <Route path="labs/results/:patientId" element={<LabResultsPage />} />
    <Route path="labs/extractions" element={<LabExtractionReviewPage />} />
    <Route path="labs/extractions/:jobId" element={<LabExtractionReviewSheet />} />
    <Route path="commissions/rates" element={<CommissionConfigPage />} />
    <Route path="commissions/statements" element={<CommissionStatementsPage />} />
    <Route path="commissions/payouts" element={<CommissionPayoutsPage />} />
    <Route path="commissions/payouts/:batchId" element={<CommissionPayoutDetailPage />} />
    <Route path="partner-failures" element={<PartnerFailures />} />
  </>
);

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------
const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <NotificationProvider>
      <IntelliHealthChatProvider>
      <FHIRProvider>
        <ClinicProvider>
            <PendingPrescriptionProvider>
              <CartProvider>
                <OrganizationProvider>
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/clinic-selection" element={<ClinicSelection />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/invite" element={<Invite />} />
                    <Route path="/practitioner-onboarding" element={<PractitionerOnboarding />} />
                    <Route path="/practitioner-onboarding/success" element={<PractitionerOnboardingSuccess />} />
                    <Route path="/onboard/clinic" element={<ClinicOnboarding />} />
                    <Route path="/onboard/clinic/complete" element={<PartialClinicOnboarding />} />
                    <Route path="/onboard/clinic/success" element={<ClinicOnboardingSuccess />} />
                    <Route path="/onboard/trial" element={<TrialSignup />} />
                    <Route path="/storefront/link/:token" element={<PrescriptionLink />} />
                    <Route path="/avs/:token" element={<PatientAvs />} />
                    <Route path="/patient-form/:token" element={<PatientFormPublic />} />
                    <Route path="/guided-order-amendment/:token" element={<GuidedOrderAmendment />} />


                    {/* Smart Targeted Intake - Public Access */}
                    <Route path="/intake/smart/:token" element={<SmartIntake />} />

                    {/* Patient Intake Routes - Public Access */}
                    <Route path="/patient/intake/womens/:token" element={<WomensIntakeLanding />} />
                    <Route path="/patient/intake/womens/:token/form" element={<WomensIntakeForm />} />
                    <Route path="/patient/intake/mens/:token" element={<MensIntakeLanding />} />
                    <Route path="/patient/intake/mens/:token/form" element={<MensIntakeForm />} />
                    <Route path="/patient/intake/weightloss/:token" element={<WeightLossIntakeLanding />} />
                    <Route path="/patient/intake/weightloss/:token/form" element={<Glp1QuizIntakeForm />} />
                    <Route path="/patient/intake/custom/:token" element={<CustomIntake />} />
                    <Route path="/patient/intake/:token" element={<PatientIntakeDefault />} />
                    <Route path="/patient/intake/:token/form" element={<PatientIntakeForm />} />

                    {/* PCOS Assessment Routes - Public Access */}
                    <Route path="/pcos" element={<PCOSLanding />} />
                    <Route path="/pcos/quiz" element={<PCOSQuiz />} />
                    <Route path="/pcos/results" element={<PCOSResults />} />

                    <Route path='/smart/launch' element={<SMARTLaunch />} />

                    {/* Telehealth - Public Patient Access */}
                    <Route path="/telehealth/join/:appointmentId" element={<PatientJoin />} />

                    {/* Patient Chat - Public Magic Link Access */}
                    <Route path="/chat/:token" element={<PatientChat />} />
                    <Route path="/:clinicSlug/chat/:token" element={<PatientChat />} />

                    {/* Public Booking Page (legacy practitioner-slug URL) */}
                    <Route path="/book/:slug" element={<PublicBookingPage />} />
                    {/* Public Booking Page V2 — clinic+type-scoped, OTP-verified */}
                    <Route
                      path="/book/c/:clinicSlug/t/:typeSlug"
                      element={<PublicBookingV2Page />}
                    />
                    {/* Public Booking landing — clinic-scoped chooser */}
                    <Route
                      path="/book/c/:clinicSlug"
                      element={<PublicBookingClinicLandingPage />}
                    />
                    <Route path="/p/:token/*" element={<PatientLinkRouter />} />

                    {/* MDI Patient Portal */}
                    <Route path="/mdi-portal/:patientId" element={<MdiPortal />} />

                    {/* Patient Consent Signing */}
                    <Route path="/patient/consent/:templateId" element={<PatientConsentSigningPage />} />

                    {/* Novamed-branded Patient Payment Page (public, token-based) */}
                    <Route path="/pay/:token" element={<PatientPaymentPage />} />

                    {/* Test Routes */}
                    <Route path="/test/booking" element={<TestBookingWidget />} />

                    {/* ── Clinic Admin ── */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <Clinic />
                        </ProtectedRoute>
                      }
                    >
                      {/* Role-aware landing: practitioners see MyDay, admin-only see ClinicDashboard.
                          Rendered via RoleAwareIndex below. */}
                      <Route index element={<RoleAwareIndex />} />
                      {StorefrontRoutes()}
                      <Route path="practitioners" element={<PractitionersPage />} />
                      <Route path="practitioners/:id" element={<PractitionerDetail />} />
                      <Route path="practitioner-staffs" element={<PractitionerStaffManagement />} />
                      {/* Three-Panel patient chart — BEFORE CommonClinicRoutes so it takes priority */}
                      <Route path="patients/:id" element={<PatientChart />} />
                      <Route path="external-patients/:id" element={<PatientChart />} />
                      {CommonClinicRoutes({ settingsRedirect: '/settings' })}
                      <Route path="roles" element={<RoleManagement />} />
                      <Route path="users" element={<UserManagement />} />
                      <Route path="admins" element={<AdminManagement />} />
                      <Route path="clinic-programs" element={<ClinicProgramsPage />} />
                      <Route path="subscriptions" element={<Subscriptions />} />
                      <Route path="staff" element={<StaffPage />} />
                      <Route path="financials" element={<FinancialsPage />} />
                      <Route path="programs-hub" element={<ProgramsPage />} />
                    </Route>

                    {/* ── Practitioner Portal ── */}
                    <Route
                      path="/practitioner-portal"
                      element={
                        <ProtectedRoutePractitioner>
                          <PractitionerPortal />
                        </ProtectedRoutePractitioner>
                      }
                    >
                      <Route index element={<MyDayDashboard />} />
                      {StorefrontRoutes()}
                      {/* Three-Panel patient chart — must be before CommonClinicRoutes so it takes priority */}
                      <Route path="patients/:id" element={<PatientChart />} />
                      <Route path="external-patients/:id" element={<PatientChart />} />
                      {CommonClinicRoutes({ settingsRedirect: '/practitioner-portal/settings' })}
                      {/* /intellihealth standalone route removed — IntelliHealth is now
                          docked in the PatientChart right panel (Three-Panel Console) */}
                    </Route>

                    {/* ── Clinic Staff Portal ── */}
                    <Route path="/clinic-staff-portal" element={
                      <StaffProtectedRoute>
                        <ClinicStaffPortal />
                      </StaffProtectedRoute>
                    }>
                      <Route index element={<ClinicDashboard />} />
                      {StorefrontRoutes()}
                      <Route path="practitioners" element={<PractitionersPage />} />
                      <Route path="practitioners/:id" element={<PractitionerDetail />} />
                      <Route path="practitioner-staffs" element={<PractitionerStaffManagement />} />
                      <Route path="patients/:id" element={<PatientChart />} />
                      <Route path="external-patients/:id" element={<PatientChart />} />
                      {CommonClinicRoutes({ settingsRedirect: '/clinic-staff-portal/settings' })}
                      <Route path="roles" element={<RoleManagement />} />
                      <Route path="users" element={<UserManagement />} />
                      <Route path="admins" element={<AdminManagement />} />
                      <Route path="clinic-programs" element={<ClinicProgramsPage />} />
                    </Route>


                    {/* ── Organization Routes ── */}
                    <Route
                      path="/org/:orgId"
                      element={
                        <ProtectedRoute>
                          <OrgLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<Navigate to="dashboard" replace />} />
                      <Route path="dashboard" element={<OrgDashboard />} />
                      <Route path="clinics" element={<OrgClinics />} />
                      <Route path="clinics/:clinicId" element={<Navigate to="../clinics" replace />} />
                      <Route path="members" element={<OrgMembers />} />
                      <Route path="settings" element={<OrgSettings />} />
                      <Route path="billing" element={<OrgBilling />} />
                      <Route path="audit-log" element={<OrgAuditLog />} />
                    </Route>

                    {/* Dev demo routes */}
                    {import.meta.env.DEV && (
                      <Route path="/demo/lab-range-bar" element={<React.Suspense fallback={null}><LabRangeBarDemo /></React.Suspense>} />
                    )}
                    <Route path="*" element={<NotFound />} />

                  </Routes>
                  </BrowserRouter>
                </TooltipProvider>
                </OrganizationProvider>
              </CartProvider>
            </PendingPrescriptionProvider>
        </ClinicProvider>
      </FHIRProvider>
      </IntelliHealthChatProvider>
      </NotificationProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
